export const locales = ['en', 'fr'] as const;
export type Locale = (typeof locales)[number];

/**
 * Single switch for the French site. Inlined at build time, so changing it requires a rebuild/redeploy.
 * Default is disabled: /fr/** is redirected (307) to /en/** in next.config.ts.
 */
export const FR_ENABLED = process.env.NEXT_PUBLIC_ENABLE_FR === 'true';
export const activeLocales: readonly Locale[] = FR_ENABLED ? locales : (['en'] as const);

export const professionSlugToFr: Record<string, string> = {
  legal: 'juridique',
  'real-estate': 'immobilier',
  finance: 'finance',
  education: 'education',
};

export const professionSlugFromFr: Record<string, string> = Object.fromEntries(
  Object.entries(professionSlugToFr).map(([en, fr]) => [fr, en])
);

/**
 * Define the structure of your dictionary here.
 * This ensures that ALL languages have the exact same keys.
 */
export type Translations = Dictionary;

export type Dictionary = {
  nav: {
    home: string;
    tools: string;
    about: string;
    newsletter: string;
    contact: string;
    insights: string;
  };
  hero: {
    title: string;
    subtitle: string;
    cta: string;
  };
  professions: {
    legal: string;
    'real-estate': string;
    finance: string;
    education: string;
    [key: string]: string;
  };
  subcategories: Record<string, string>;
  categories: Record<string, string>;
  faq: Array<{ q: string; a: string }>;
  footer: {
    directory: string;
    platform: string;
    legal: string;
    help: string;
    tagline: string;
    stayInLoop: string;
    subscribe: string;
    noticePrefix: string;
    noticeText: string;
    copyright: string;
    madeWithLove: string;
    links: {
      aiForLegal: string;
      aiForRealEstate: string;
      aiForFinance: string;
      aiForEducation: string;
      popularTools: string;
      howWeReview: string;
      latestInsights: string;
      submitTool: string;
      newsletter: string;
      privacy: string;
      terms: string;
      disclaimer: string;
      cookiePolicy: string;
      contactUs: string;
      helpCenter: string;
      reportProblem: string;
    };
  };
  professionPage: {
    titleTemplate: string;
    metaDescriptionTemplate: string;
    h1Template: string;
    introTemplate: string;
    browseByCategory: string;
    toolsCount: string;
    jurisdictionCritical: string;
    viewTools: string;
    searchPlaceholder: string;
    categoryFilter: string;
    pricingFilter: string;
    filters: string;
    newBadge: string;
    views: string;
    compare: string;
    showMoreTools: string;
    noToolsFound: string;
    tryAdjustingFilters: string;
    clearAllFilters: string;
  };
  subcategoryPage: {
    titleTemplate: string;
    metaDescriptionTemplate: string;
    jurisdictionWarning: string;
    h1Template: string;
    introTemplate: string;
  };
  toolBadges: {
    global: string;
    paid: string;
    sponsored: string;
  };
};

export const translations: Record<Locale, Dictionary> = {
  en: {
    nav: {
      home: 'Home',
      tools: 'Tools',
      about: 'About',
      newsletter: 'Newsletter',
      contact: 'Contact',
      insights: 'Insights',
    },
    hero: {
      title: 'GuideToReviews: AI Tools for Professionals',
      subtitle:
        'Curated directory of AI solutions for legal, real estate, finance, and education — find the right tool to boost your productivity.',
      cta: 'Browse All Professions',
    },
    professions: {
      legal: 'Legal',
      'real-estate': 'Real Estate',
      finance: 'Accounts & Finance',
      education: 'Education',
    },
    subcategories: {
      'patent-ai-tools': 'Patent AI Tools',
      'copyright-ai-tools': 'Copyright AI Tools',
      'trademark-ai-tools': 'Trademark AI Tools',
      'privacy-finance': 'Privacy Finance',
      'carbon-esg-finance': 'Carbon & ESG Finance',
    },
    categories: {
      'contract-review': 'Contract Review',
      'legal-research-tools': 'Legal Research',
      'document-automation': 'Document Automation',
      'litigation-analytics': 'Litigation Analytics',
      'compliance-ai': 'Compliance AI',
      'virtual-staging': 'Virtual Staging',
      'property-valuation': 'Property Valuation',
      'lead-generation': 'Lead Generation',
      'financial-modeling': 'Financial Modeling',
      'audit-compliance-ai': 'Audit & Compliance',
      'tax-filing-ai': 'Tax Filing AI',
      'curriculum-design': 'Curriculum Design',
      'grading-feedback': 'Grading & Feedback',
      'student-tutoring': 'Student Tutoring',
    },
    faq: [
      {
        q: 'Are the AI tools on GuideToReviews free to use?',
        a: 'Each tool has its own pricing model — Free, Freemium, or Paid. We clearly label pricing on every listing so you can filter by budget before visiting the tool website.',
      },
      {
        q: 'How do you verify tools before listing them?',
        a: 'Every tool listed on GuideToReviews has an active working website confirmed at time of listing. We check that the product exists and is accessible.',
      },
      {
        q: 'Are these tools suitable for my country or jurisdiction?',
        a: 'Many legal and tax AI tools are jurisdiction-specific. We display a jurisdiction badge on every tool page.',
      },
      {
        q: 'Does GuideToReviews earn commission from listed tools?',
        a: 'Some tools in our directory have affiliate programs. Where we have an affiliate relationship, we disclose this clearly on the tool page.',
      },
      {
        q: 'Can I submit my AI tool for listing?',
        a: 'Yes. Use the Submit a Tool page to submit your tool for review. All submissions are manually reviewed before listing.',
      },
    ],
    footer: {
      directory: 'Directory',
      platform: 'Platform',
      legal: 'Legal',
      help: 'Help & Support',
      tagline: 'The authoritative directory of artificial intelligence tools for high-trust professionals.',
      stayInLoop: 'Get curated AI tool updates for professionals delivered to your inbox.',
      subscribe: 'Subscribe',
      noticePrefix: 'Professional Disclaimer:',
      noticeText: 'Information provided on GuideToReviews is for informational purposes only and does not constitute legal, financial, or real estate advice.',
      copyright: '© 2026 GuideToReviews. All rights reserved.',
      madeWithLove: 'Built with integrity for licensed professionals.',
      links: {
        aiForLegal: 'Legal AI',
        aiForRealEstate: 'Real Estate AI',
        aiForFinance: 'Finance AI',
        aiForEducation: 'Education AI',
        popularTools: 'All Tools',
        howWeReview: 'How We Review',
        latestInsights: 'Insights & Trends',
        submitTool: 'Submit a Tool',
        newsletter: 'Newsletter',
        privacy: 'Privacy Policy',
        terms: 'Terms of Service',
        disclaimer: 'Disclaimer',
        cookiePolicy: 'Cookie Policy',
        contactUs: 'Contact Us',
        helpCenter: 'Help Center',
        reportProblem: 'Report an Issue',
      },
    },
    professionPage: {
      titleTemplate: 'Best AI Tools for {profession} ({year}) | GuideToReviews',
      metaDescriptionTemplate: 'Discover top AI tools for {profession}. Curated reviews, pricing, features, and comparisons.',
      h1Template: 'AI Tools for {profession}',
      introTemplate: 'Explore verified artificial intelligence software curated for {profession} workflows and practices.',
      browseByCategory: 'Browse by Category',
      toolsCount: '{count} tools',
      jurisdictionCritical: 'Jurisdiction Critical',
      viewTools: 'View Tools',
      searchPlaceholder: 'Search tools...',
      categoryFilter: 'All Categories',
      pricingFilter: 'All Pricing',
      filters: 'Filters',
      newBadge: 'New',
      views: '{count} views',
      compare: 'Compare',
      showMoreTools: 'Show More Tools ({count} remaining)',
      noToolsFound: 'No tools found',
      tryAdjustingFilters: 'Try adjusting your filters or search terms.',
      clearAllFilters: 'Clear All Filters',
    },
    subcategoryPage: {
      titleTemplate: 'Best {category} AI Tools for {profession} ({year}) | GuideToReviews',
      metaDescriptionTemplate: 'Discover top {category} AI tools for {profession}. Verified reviews, pricing, and workflow guides.',
      jurisdictionWarning: 'Jurisdiction Critical — Local compliance applies',
      h1Template: '{category} AI Tools',
      introTemplate: 'Compare and discover top-rated {category} tools built for {profession} workflows.',
    },
    toolBadges: {
      global: 'Global',
      paid: 'Paid',
      sponsored: 'Sponsored',
    },
  },
  fr: {
    nav: {
      home: 'Accueil',
      tools: 'Outils',
      about: 'À propos',
      newsletter: 'Bulletin',
      contact: 'Contact',
      insights: 'Insights',
    },
    hero: {
      title: 'GuideToReviews : Outils IA pour Professionnels',
      subtitle:
        'Annuaire d’outils IA pour le juridique, l’immobilier, la finance et l’éducation — boostez votre productivité.',
      cta: 'Découvrir les Professions',
    },
    professions: {
      legal: 'Juridique',
      'real-estate': 'Immobilier',
      finance: 'Comptabilité & Finance',
      education: 'Éducation',
    },
    subcategories: {
      'patent-ai-tools': "Outils d'IA pour les brevets",
      'copyright-ai-tools': "Outils d'IA pour le droit d'auteur",
      'trademark-ai-tools': "Outils d'IA pour les marques",
      'privacy-finance': 'Confidentialité financière',
      'carbon-esg-finance': 'Finance carbone & ESG',
    },
    categories: {
      'contract-review': 'Examen de contrats',
      'legal-research-tools': 'Recherche juridique',
      'document-automation': 'Automatisation documentaire',
      'litigation-analytics': 'Analyse des litiges',
      'compliance-ai': 'Conformité IA',
      'virtual-staging': 'Home staging virtuel',
      'property-valuation': 'Évaluation immobilière',
      'lead-generation': 'Génération de prospects',
      'financial-modeling': 'Modélisation financière',
      'audit-compliance-ai': 'Audit & Conformité',
      'tax-filing-ai': 'Déclaration fiscale IA',
      'curriculum-design': 'Conception de programmes',
      'grading-feedback': 'Évaluation & Rétroaction',
      'student-tutoring': 'Tutorat étudiant',
    },
    faq: [
      {
        q: 'Les outils IA sur GuideToReviews sont-ils gratuits ?',
        a: 'Chaque outil a son propre modèle de prix — Gratuit, Freemium ou Payant. Nous indiquons clairement le prix sur chaque fiche.',
      },
      {
        q: 'Comment vérifiez-vous les outils ?',
        a: 'Chaque outil listé possède un site web actif. Nous vérifions que le produit existe et est accessible aux professionnels.',
      },
      {
        q: 'Ces outils sont-ils adaptés à ma juridiction ?',
        a: 'De nombreux outils juridiques sont spécifiques à un pays. Nous affichons un badge de juridiction sur chaque page d’outil.',
      },
      {
        q: 'GuideToReviews touche-t-il des commissions ?',
        a: 'Certains outils ont des programmes d’affiliation. Cela n’influence jamais nos classements ou nos avis indépendants.',
      },
      {
        q: 'Puis-je soumettre mon outil IA ?',
        a: 'Oui. Utilisez la page de soumission. Toutes les propositions sont examinées manuellement sous 2 à 3 jours.',
      },
    ],
    footer: {
      directory: 'Annuaire',
      platform: 'Plateforme',
      legal: 'Mentions légales',
      help: 'Aide & Support',
      tagline: "L'annuaire de référence des outils d'intelligence artificielle pour les professionnels de confiance.",
      stayInLoop: 'Recevez notre sélection d’outils IA professionnels directement par email.',
      subscribe: "S'abonner",
      noticePrefix: 'Avertissement professionnel :',
      noticeText: 'Les informations fournies sur GuideToReviews sont à titre purement informatif et ne constituent pas un conseil juridique, financier ou immobilier.',
      copyright: '© 2026 GuideToReviews. Tous droits réservés.',
      madeWithLove: 'Conçu avec intégrité pour les professionnels certifiés.',
      links: {
        aiForLegal: 'IA Juridique',
        aiForRealEstate: 'IA Immobilier',
        aiForFinance: 'IA Finance',
        aiForEducation: 'IA Éducation',
        popularTools: 'Tous les outils',
        howWeReview: 'Méthodologie',
        latestInsights: 'Insights & Tendances',
        submitTool: 'Soumettre un outil',
        newsletter: 'Bulletin',
        privacy: 'Politique de confidentialité',
        terms: "Conditions d'utilisation",
        disclaimer: 'Clause de non-responsabilité',
        cookiePolicy: 'Politique des cookies',
        contactUs: 'Contactez-nous',
        helpCenter: "Centre d'aide",
        reportProblem: 'Signaler un problème',
      },
    },
    professionPage: {
      titleTemplate: 'Meilleurs Outils IA pour {profession} ({year}) | GuideToReviews',
      metaDescriptionTemplate: 'Découvrez les meilleurs outils IA pour {profession}. Avis d’experts, tarifs, fonctionnalités et comparatifs.',
      h1Template: 'Outils IA pour {profession}',
      introTemplate: 'Explorez des logiciels d’intelligence artificielle vérifiés pour les professionnels en {profession}.',
      browseByCategory: 'Parcourir par catégorie',
      toolsCount: '{count} outils',
      jurisdictionCritical: 'Juridiction critique',
      viewTools: 'Voir les outils',
      searchPlaceholder: 'Rechercher des outils...',
      categoryFilter: 'Toutes les catégories',
      pricingFilter: 'Tous les tarifs',
      filters: 'Filtres',
      newBadge: 'Nouveau',
      views: '{count} vues',
      compare: 'Comparer',
      showMoreTools: "Afficher plus d'outils ({count} restants)",
      noToolsFound: 'Aucun outil trouvé',
      tryAdjustingFilters: 'Essayez d’ajuster vos filtres ou vos termes de recherche.',
      clearAllFilters: 'Effacer tous les filtres',
    },
    subcategoryPage: {
      titleTemplate: 'Meilleurs Outils IA de {category} pour {profession} ({year}) | GuideToReviews',
      metaDescriptionTemplate: 'Découvrez les meilleurs outils IA de {category} pour {profession}. Avis vérifiés, tarifs et guides métier.',
      jurisdictionWarning: 'Juridiction critique — Conformité locale requise',
      h1Template: 'Outils IA de {category}',
      introTemplate: 'Comparez et découvrez les meilleurs outils de {category} adaptés aux exigences en {profession}.',
    },
    toolBadges: {
      global: 'Mondial',
      paid: 'Payant',
      sponsored: 'Sponsorisé',
    },
  },
};

export function getLocaleFromPathname(pathname: string): Locale {
  return pathname === '/fr' || pathname.startsWith('/fr/') ? 'fr' : 'en';
}

export function toEnglishProfessionSlug(slug: string): string {
  const cleanSlug = slug.replace(/^ai-for-/, '');
  return professionSlugFromFr[cleanSlug] ?? cleanSlug;
}

export function toFrenchProfessionSlug(slug: string): string {
  const cleanSlug = slug.replace(/^ai-for-/, '');
  return professionSlugToFr[cleanSlug] ?? cleanSlug;
}

export function localizePathname(pathname: string, locale: Locale): string {
  const hasFr = pathname === '/fr' || pathname.startsWith('/fr/');
  const hasEn = pathname === '/en' || pathname.startsWith('/en/');
  
  let bare = pathname;
  if (hasFr) {
    bare = pathname === '/fr' ? '/' : pathname.replace(/^\/fr\//, '/');
  } else if (hasEn) {
    bare = pathname === '/en' ? '/' : pathname.replace(/^\/en\//, '/');
  }

  const aiForMatch = bare.match(/^\/ai-for-([^/]+)(\/.*)?$/);
  if (aiForMatch) {
    const slug = aiForMatch[1];
    const rest = aiForMatch[2] ?? '';
    const localizedSlug =
      locale === 'fr' ? toFrenchProfessionSlug(toEnglishProfessionSlug(slug)) : toEnglishProfessionSlug(slug);
    const localizedPath = `/ai-for-${localizedSlug}${rest}`;
    return locale === 'fr' ? `/fr${localizedPath}` : `/en${localizedPath}`;
  }

  return locale === 'fr' ? (bare === '/' ? '/fr' : `/fr${bare}`) : (bare === '/' ? '/en' : `/en${bare}`);
}

/**
 * Build the Next.js `alternates` metadata (canonical + hreflang languages) for a page.
 * While French is disabled, the FR alternate is dropped and the canonical is always the EN URL.
 * Single source of truth for hreflang/canonical across all localized pages.
 */
export function buildAlternates(
  locale: Locale,
  enUrl: string,
  frUrl: string
): { canonical: string; languages: Record<string, string> } {
  if (!FR_ENABLED) {
    return {
      canonical: enUrl,
      languages: {
        en: enUrl,
        'x-default': enUrl,
      },
    };
  }

  return {
    canonical: locale === 'fr' ? frUrl : enUrl,
    languages: {
      en: enUrl,
      fr: frUrl,
      'x-default': enUrl,
    },
  };
}

/**
 * OpenGraph locale string, honoring the FR flag.
 */
export function ogLocale(locale: Locale): 'en_US' | 'fr_FR' {
  return FR_ENABLED && locale === 'fr' ? 'fr_FR' : 'en_US';
}
